import type { BundleSplitterTaskStatusEnum } from "@/api/generated/types.gen";

import type { BundleRowStatus, BundleUploadRow } from "../store/useBundleUploads";

/**
 * Bundle-splitter tasks the splitter hasn't finished with: recovered into the
 * pop-up (STP-77), and still holding their file name (MDRS-550). The task list
 * types its status as a plain string, so the values are checked here.
 */
export const TASK_IN_FLIGHT_STATUSES: ReadonlySet<string> =
  new Set<BundleSplitterTaskStatusEnum>(["PENDING", "PROCESSING"]);

/** One of this user's earlier bundle-splitter tasks on the case. */
export type PriorUpload = {
  taskId: string;
  fileName: string;
  fileSize: number | null;
  status: string;
};

export type UploadConflict = {
  /** Picked again: already in the list for the case, or already uploaded. */
  duplicate?: "list" | "earlier";
  /**
   * A same-name file for the case is still on its way to the splitter. The
   * blob path is the bare file name, so starting would overwrite it.
   */
  isStartBlocked: boolean;
  /**
   * Earlier uploads in flight on the server that hold the block. They aren't
   * rows here, so only polling their status (which updates the server's
   * record, and then the task list) can lift it.
   */
  waitingOnTaskIds: string[];
};

// Until the splitter has read the bundle, its blob mustn't be overwritten.
const ROW_IN_FLIGHT: BundleRowStatus[] = ["uploading", "preparing", "splitting"];

const isSameFile = (a: File, b: File) => a.name === b.name && a.size === b.size;

/**
 * MDRS-550: rows of one case that shouldn't start as-is, keyed by row id.
 * Only rows not started yet are considered (a failed attempt can't restart
 * over an in-flight same-name file either), and a listed row's own task never
 * counts as an earlier upload. A task the user dismissed still holds a
 * re-pick: dismissing only hides the row, and the splitter keeps running it.
 * Name + size is what the frontend can compare — a renamed copy, or someone
 * else's upload, needs the server (MDRS-406).
 */
export const findUploadConflicts = (
  rows: BundleUploadRow[],
  priorUploads: PriorUpload[]
): Map<string, UploadConflict> => {
  const listedTaskIds = new Set(
    rows.flatMap((row) => (row.taskId ? [row.taskId] : []))
  );
  const prior = priorUploads.filter((upload) => !listedTaskIds.has(upload.taskId));
  const conflicts = new Map<string, UploadConflict>();

  rows.forEach((row, index) => {
    if (row.taskId || (row.status !== "ready" && row.status !== "failed")) return;
    const { name, size } = row.file;

    const isBlockedByRow = rows.some(
      (other) =>
        other.id !== row.id &&
        other.file.name === name &&
        ROW_IN_FLIGHT.includes(other.status)
    );
    const waitingOnTaskIds = isBlockedByRow
      ? []
      : prior
          .filter(
            (upload) =>
              upload.fileName === name && TASK_IN_FLIGHT_STATUSES.has(upload.status)
          )
          .map((upload) => upload.taskId);
    const isStartBlocked = isBlockedByRow || waitingOnTaskIds.length > 0;

    let duplicate: UploadConflict["duplicate"];
    if (row.status === "ready") {
      if (rows.slice(0, index).some((other) => isSameFile(other.file, row.file))) {
        duplicate = "list";
      } else if (
        prior.some(
          (upload) =>
            upload.status === "SUCCESS" &&
            upload.fileName === name &&
            upload.fileSize === size
        )
      ) {
        duplicate = "earlier";
      }
    }

    if (duplicate || isStartBlocked)
      conflicts.set(row.id, { duplicate, isStartBlocked, waitingOnTaskIds });
  });
  return conflicts;
};
